import { CreditCard } from "@/api/credit-cards/credit-cards";
import WhiteLabelLogo from "@/components/_module/loading/white-label-logo";
import CardFailedPage from "@/components/pages/card/failed";
import DepositSuccessPage from "@/components/pages/deposit/success";
import PageLoader from "@/components/ui/PageLoader";
import PageTransition from "@/components/ui/PageTransition";
import { transactionKeys } from "@/features/transactions/keys";
import useIframeTransferIn from "@/features/transactions/useIframeTransferIn";
import useTransactionPreview from "@/features/transactions/useTransactionPreview";
import { queryClient } from "@/query-client";
import { useNavigate } from "@tanstack/react-router";
import React from "react";
import ReactDOM from "react-dom";
import { z } from "zod";
import ReviewDepositUI from "./ReviewDepositUI";
import TransferInPaymentIframe from "./TransferInPaymentIframe";

const RiskLevelValueSchema = z.enum(["high", "medium", "low", "unknown"]);
const RiskLevelSchema = z.object({
	details: z
		.object({
			risk_level: RiskLevelValueSchema,
		})
		.nullable()
		.optional(),
});
type RiskLevel = z.infer<typeof RiskLevelValueSchema>;

const RISK_LEVEL_ERROR_MESSAGES: Record<RiskLevel, React.ReactNode> = {
	high: (
		<div className="space-y-3 text-left">
			<p>
				This transaction requires an additional security review before it can be
				completed.
			</p>
			<p>
				Please check your email for further instructions. Do not retry this
				transaction until you complete the steps in that email.
			</p>
		</div>
	),
	medium: (
		<div className="space-y-3 text-left">
			<p>
				This transaction could not be approved and may require confirmation with
				your bank.
			</p>
			<p>
				Please check your email for further instructions before trying the
				transaction again.
			</p>
		</div>
	),
	low: (
		<div className="space-y-3 text-left">
			<p>
				This transaction was declined due to a temporary issue or a details
				verification check.
			</p>
			<p>
				Please check your email for further instructions, then follow those
				steps before attempting this transaction again.
			</p>
		</div>
	),
	unknown: (
		<div className="space-y-3 text-left">
			<p>This transaction could not be completed.</p>
			<p>
				Please check your email for further instructions before trying again.
			</p>
		</div>
	),
} as const;

// The card may have been charged but the outcome couldn't be confirmed: it is settled
// automatically, so the guest must not retry (that would charge the card again).
const UNCONFIRMED_MESSAGE = (
	<div className="space-y-3 text-left">
		<p>We couldn't confirm this payment yet.</p>
		<p>
			If your card was charged, the funds will appear in your Activity shortly.
			Please don't retry this transfer; contact support if they don't appear
			within an hour.
		</p>
	</div>
);

// The bank's verification (3DS) ended without authenticating the card: the iframe never charged
// it, so no transaction, no email. Cancelled = the guest pressed Cancel on the bank's challenge.
const ThreeDsNotAuthenticatedSchema = z.object({
	error: z.object({
		name: z.enum([
			"cybersource_CONSUMER_AUTHENTICATION_FAILED",
			"3ds_auth_validation_failed",
		]),
		details: z
			.object({ cancelled_by_cardholder: z.boolean().optional() })
			.passthrough()
			.nullable()
			.optional(),
	}),
});

const getThreeDsNotAuthenticated = (
	error: unknown,
): "cancelled" | "failed" | null => {
	const parsed = ThreeDsNotAuthenticatedSchema.safeParse(error);
	if (!parsed.success) return null;
	return parsed.data.error.details?.cancelled_by_cardholder === true
		? "cancelled"
		: "failed";
};

const THREE_DS_NOT_AUTHENTICATED_MESSAGES = {
	cancelled: (
		<div className="space-y-3 text-left">
			<p>You cancelled the verification with your bank.</p>
			<p>Your card was not charged. You can try again whenever you're ready.</p>
		</div>
	),
	failed: (
		<div className="space-y-3 text-left">
			<p>Your bank couldn't verify this payment.</p>
			<p>
				Your card was not charged. Please try again, or use a different card.
			</p>
		</div>
	),
} as const;

const getRiskLevel = (error: unknown): RiskLevel => {
	const parsed = RiskLevelSchema.safeParse(error);
	return parsed.data?.details?.risk_level ?? "unknown";
};

const ErrorTransactionSchema = z.object({
	transaction_no: z.number(),
	details: z
		.object({
			transaction_no: z.coerce.number().optional(),
		})
		.nullable()
		.optional(),
});

const getErrorTransactionNumber = (error: unknown) => {
	const parsed = ErrorTransactionSchema.safeParse(error);
	if (parsed.success) {
		return parsed.data.transaction_no;
	}
	return undefined;
};

interface ReviewDepositContainerProps {
	amount: number;
	selectedCard: CreditCard;
	guestId: number;
	cardId: string;
	locationId: number;
	onBack: () => void;
	promo: string;
}

export const Portal: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	if (typeof window === "undefined") return null;
	const portalRoot =
		document.getElementById("portal-root") ||
		(() => {
			const el = document.createElement("div");
			el.id = "portal-root";
			document.body.appendChild(el);
			return el;
		})();
	return ReactDOM.createPortal(children, portalRoot);
};

const ReviewDepositContainer: React.FC<ReviewDepositContainerProps> = ({
	amount,
	selectedCard,
	guestId,
	cardId,
	locationId,
	onBack,
	promo,
}) => {
	const navigate = useNavigate();
	const transferIn = useIframeTransferIn({
		guestId,
		cardId,
		locationId,
		onSettled: () => {
			queryClient.refetchQueries({
				queryKey: transactionKeys.all(),
			});
		},
	});

	const transactionPreviewQuery = useTransactionPreview(
		amount,
		locationId,
		promo,
	);

	const submitPayment = () => {
		transferIn.start({
			guestId,
			cardId,
			amount,
			locationId,
			promo_code: promo,
		});
	};

	if (transferIn.status === "success" && transferIn.data) {
		return (
			<div className="">
				<DepositSuccessPage transferResponse={transferIn.data} />
			</div>
		);
	}

	if (transferIn.status === "unconfirmed") {
		return (
			<CardFailedPage
				errorMessage={UNCONFIRMED_MESSAGE}
				tryAgainLabel="Back to Home"
				onTryAgainClick={() => {
					navigate({ to: "/main/home" });
				}}
				onClose={() => {
					navigate({ to: "/main/home" });
				}}
			/>
		);
	}

	if (transferIn.status === "error") {
		const error = transferIn.error;
		const threeDsNotAuthenticated = getThreeDsNotAuthenticated(error);
		const riskLevel = getRiskLevel(error);
		const errorMessage = threeDsNotAuthenticated
			? THREE_DS_NOT_AUTHENTICATED_MESSAGES[threeDsNotAuthenticated]
			: RISK_LEVEL_ERROR_MESSAGES[riskLevel];
		const transactionNumber = getErrorTransactionNumber(error);

		return (
			<CardFailedPage
				errorMessage={errorMessage}
				transactionNumber={transactionNumber}
				onTryAgainClick={() => {
					navigate({ to: "/main/deposit" });
				}}
			/>
		);
	}

	return (
		<div className="h-full">
			<PageTransition className="h-full ">
				<PageLoader query={transactionPreviewQuery}>
					{(data) => {
						return (
							<ReviewDepositUI
								transactionPreview={data}
								onBack={onBack}
								onSubmit={() => submitPayment()}
								amount={amount}
								selectedCard={selectedCard}
								promo={promo}
							/>
						);
					}}
				</PageLoader>
			</PageTransition>

			{transferIn.status === "charging" && transferIn.iframe && (
				<Portal>
					{/* Above the chat button (z-[9999]) and the nav; scrolls so the bank's challenge
					    (up to 600px) fits on short screens. */}
					<div
						data-testid="loading-overlay"
						className="fixed inset-0 z-[10000] overflow-y-auto bg-black/60"
					>
						<div className="flex min-h-full items-center justify-center px-4 py-6">
							<TransferInPaymentIframe
								locationId={locationId}
								iframeAccessToken={transferIn.iframe.accessToken}
								payment={transferIn.iframe.payment}
								onApproved={transferIn.onIframeApproved}
								onDeclined={transferIn.onIframeDeclined}
								onTimeout={transferIn.onIframeTimeout}
							/>
						</div>
					</div>
				</Portal>
			)}

			{(transferIn.status === "preparing" ||
				transferIn.status === "completing") && (
				<Portal>
					<div
						data-testid="loading-overlay"
						className="fixed top-0 left-0 z-[10000] h-screen w-screen flex items-center justify-center bg-black/60"
					>
						<WhiteLabelLogo />
					</div>
				</Portal>
			)}
		</div>
	);
};

export default ReviewDepositContainer;
